(ns common.ui.validation.view
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.spec.alpha :as spec]
            [common.widgets.form-field :as widgets.form-field]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(spec/def :validation/schema (spec/or :schema-definition keyword?
                                      :custom-schema vector?))

(spec/def :validation/render-type #{:unordered-list :alert-list :summary :dot})
(spec/def :validation/summary-type #{:error+warning :error :warning})

(spec/def ::coll-of-string (spec/nilable (spec/coll-of string?)))
(spec/def :form-field/save-errors ::coll-of-string)
(spec/def :form-field/publish-errors ::coll-of-string)
(spec/def :form-field/warnings ::coll-of-string)

(spec/def :form-field/props
  (spec/nilable
   (spec/keys :opt-un [:form-field/save-errors
                       :form-field/publish-errors
                       :form-field/warnings])))

(defui form-field
  "Returns hiccup for form-field component of the
   content-configuration page"
  [{save-errors :save-blocking publish-errors :publish-blocking warnings :warning children :children :as props}]
  {:pre [(spec/assert :form-field/props props)]}
  ($ widgets.form-field/form-field
     (merge (dissoc props :save-blocking :publish-blocking :warnings :children)
            {:err-msg (first save-errors)})

     children

     (when publish-errors
       ($ Stack {:component "ul"
                 :data-component :form-field__publish-errors
                 :sx #js {:pl 0
                          :color "error.main"
                          :fontWeight "600"
                          :fontSize "12px"
                          :listStyle "none"}}
          (for [err publish-errors]
            ($ :li {:key err :data-testid :publish-error} err))))

     (when warnings
       ($ Stack {:component "ul"
                 :data-component :form-field__warnings
                 :sx #js {:pl 0
                          :color "warning.main"
                          :fontWeight "600"
                          :fontSize "12px"
                          :listStyle "none"}}
          (for [err warnings]
            ($ :li {:key err :data-testid :warning} err))))))

(defn- props [component-id summary-type]
  {:data-component component-id
   :data-validation-type summary-type
   :color (get {:error "error"
                :error+warning "error"
                :warning "warning"} summary-type)})

(defui unordered-validation-list [{:keys [summary-type
                                          validation-results]}]
  {:pre [(spec/assert #{:error :warning} summary-type)]}
  (let [props (props :validation-unordered-list summary-type)]
    ($ Stack (merge (dissoc props :color)
                    {:component "ul"
                     :sx #js {:color (str (:color props) ".main")
                              :my 0
                              :pl 0
                              :fontWeight "600"
                              :fontSize "12px"
                              :listStyle "none"}})
       (for [{:keys [code message]} validation-results]
         ($ :li {:key code
                 :data-component :validation-list-item
                 :data-validation-type summary-type
                 :data-validation-code code}
            message)))))

(defui alert-validation-list [{:keys [summary-type
                                      validation-results]}]
  {:pre [(spec/assert :validation/summary-type summary-type)]}
  (let [props (props :alert-list summary-type)]
    ($ Stack (merge (dissoc props :color)
                    {:component "ul"
                     :spacing 1
                     :sx #js {:my 0
                              :pl 0
                              :listStyle "none"}})
       (for [{:keys [code message]} validation-results]
         ($ :li {:key code
                 :data-component :validation-list-item
                 :data-validation-type summary-type
                 :data-validation-code code}
            ($ Alert {:severity (:color props)}
               message))))))

(defn summary-type
  [errors warnings]
  {:post [#(spec/assert :validation/summary-type %)]}
  (cond
    (and errors warnings) :error+warning
    (and errors (not warnings)) :error
    (and warnings (not errors)) :warning))

(def summary-message
  {:error+warning "There are errors/warnings"
   :error "There are errors"
   :warning "There are warnings"})

(defui validation-summary [{:keys [summary-type]}]
  {:pre [(spec/assert :validation/summary-type summary-type)]}
  (let [props (props :validation-summary summary-type)]
    ($ Typography (merge (dissoc props :color)
                         {:sx #js {:color (str (:color props) ".main")
                                   :fontWeight "600"
                                   :fontSize "12px"}})
       (summary-message summary-type))))

(defui validation-dot [{:keys [summary-type]}]
  {:pre [(spec/assert :validation/summary-type summary-type)]}
  ($ Icon (merge (props :validation-dot summary-type)
                 {:sx #js {:fontSize "8px"}})
     "circle"))

(defui view-results [{:keys [stack-props
                             render-type
                             validation-results-sub]}]
  {:pre [(spec/assert :validation/render-type render-type)]}
  (let [{publish-errors :publish-blocking
         warnings :warning} (use-subscribe validation-results-sub)]
    (when (or publish-errors warnings)
      ($ Stack stack-props
         (case render-type
           :summary
           ($ validation-summary {:summary-type (summary-type publish-errors warnings)})

           :unordered-list
           ($ :<>
              (when publish-errors ($ unordered-validation-list {:summary-type :error
                                                                 :validation-results publish-errors}))
              (when warnings ($ unordered-validation-list {:summary-type :warning
                                                           :validation-results warnings})))

           :alert-list
           ($ :<>
              (when publish-errors ($ alert-validation-list {:summary-type :error
                                                             :validation-results publish-errors}))
              (when warnings ($ alert-validation-list {:summary-type :warning
                                                       :validation-results warnings})))

           :dot
           ($ validation-dot {:summary-type (summary-type publish-errors warnings)}))))))
